import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ObjectPage, ObjectPageTitle, ObjectPageHeader, ObjectPageSection, Title, Label, Text, Button, MessageStrip, BusyIndicator, FlexBox, Form, FormItem, ObjectStatus } from '@ui5/webcomponents-react';
import type { ActionDef, FieldDef, ScreenDef } from '@b1links/core';
import { api, CODE_ACTIONS, type EntityMeta, type Rec } from '../api/client';
import { useAuth } from '../store/auth';
import { FieldControl } from '../render/FieldControl';
import { CollectionTable } from '../render/CollectionTable';
import { AttachmentsPanel } from '../render/AttachmentsPanel';
import { enumLabel, findField, formatValue, labelFor, recordKey } from '../render/format';
import { useIsMobile } from '../hooks/useIsMobile';

type Mode = 'display' | 'edit' | 'create';

/** Evaluate the tiny "when" language: "Field eq 'v'", "Field ne 'v'", combined with " and ". */
function when(expr: string | undefined, rec: Rec): boolean {
  if (!expr) return true;
  return expr.split(/\s+and\s+/i).every(part => {
    const m = part.trim().match(/^(\w+)\s+(eq|ne)\s+'?([^']*)'?$/i);
    if (!m) return true;
    const v = String(rec[m[1]] ?? '');
    return m[2].toLowerCase() === 'eq' ? v === m[3] : v !== m[3];
  });
}

function defaults(def: ScreenDef, user: string): Rec {
  const r: Rec = {};
  const today = new Date().toISOString().slice(0, 10);
  for (const s of def.form.sections) if (s.kind === 'fields') for (const f of s.fields) if (f.default !== undefined)
    r[f.field] = f.default === '$today' ? today : f.default === '$user' ? user : f.default;
  return r;
}

/** Generic Fiori object page (display / edit / create) rendered from a ScreenDef. Routes: /s/:screenId/new, /s/:screenId/:key */
export function FormScreen() {
  const { screenId = '', key } = useParams();
  const isNew = key === 'new' || key === undefined;
  const nav = useNavigate();
  const qc = useQueryClient();
  const { principal, can } = useAuth();
  const isMobile = useIsMobile();
  const [mode, setMode] = useState<Mode>(isNew ? 'create' : 'display');
  const [draft, setDraft] = useState<Rec>({});
  const [msg, setMsg] = useState<{ design: 'Positive' | 'Negative' | 'Information'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const def = useQuery({ queryKey: ['screen', screenId], queryFn: () => api.screen(screenId) });
  const d = def.data;
  const meta = useQuery({ queryKey: ['meta', d?.entity], queryFn: () => api.entityMeta(d!.entity), enabled: !!d, staleTime: Infinity });
  const rec = useQuery({ queryKey: ['rec', d?.entity, key], queryFn: () => api.get(d!.entity, key!), enabled: !!d && !isNew });

  useEffect(() => { if (d && isNew) { setDraft(defaults(d, principal?.user ?? '')); setMode('create'); } }, [d, isNew, principal?.user]);
  useEffect(() => { if (rec.data && mode === 'display') setDraft(rec.data); }, [rec.data, mode]);

  const keyField = meta.data?.keys[0];
  // An administrator's screen grant decides view vs edit; otherwise the role's entity permission does.
  const canEdit = !!d && (d.access ? d.access === 'edit' : can(`${d.entity}.Edit`));
  const currency = (draft.DocCurrency ?? draft.Currency) as string | undefined;
  const patch = (p: Rec) => setDraft(x => ({ ...x, ...p }));

  const fieldDefs = useMemo(() => d?.form.sections.flatMap(s => (s.kind === 'fields' ? s.fields : [])) ?? [], [d]);
  const missing = () => fieldDefs.filter(f => f.required && (draft[f.field] == null || draft[f.field] === '')).map(f => labelFor(f.field, findField(meta.data, f.field), f.label));

  const save = async () => {
    if (!d) return;
    const miss = missing(); if (miss.length) { setMsg({ design: 'Negative', text: `Required: ${miss.join(', ')}` }); return; }
    setBusy(true); setMsg(null);
    try {
      if (mode === 'create') {
        const created = await api.create(d.entity, draft);
        qc.invalidateQueries({ queryKey: ['list', screenId] });
        setMsg({ design: 'Positive', text: 'Created' });
        nav(`/s/${screenId}/${encodeURIComponent(recordKey(meta.data, created) ?? String(created[keyField!]))}`, { replace: true });
        setMode('display');
      } else {
        const body: Rec = {};
        for (const f of fieldDefs) if (!f.readonly && !findField(meta.data, f.field)?.isKey && draft[f.field] !== rec.data?.[f.field]) body[f.field] = draft[f.field];
        for (const s of d.form.sections) if (s.kind === 'collection' && JSON.stringify(draft[s.collection]) !== JSON.stringify(rec.data?.[s.collection])) body[s.collection] = draft[s.collection];
        if (Object.keys(body).length) await api.update(d.entity, key!, body, !!d.form.replaceCollections);
        await qc.invalidateQueries({ queryKey: ['rec', d.entity, key] }); qc.invalidateQueries({ queryKey: ['list', screenId] });
        setMode('display'); setMsg({ design: 'Positive', text: 'Saved' });
      }
    } catch (e) { setMsg({ design: 'Negative', text: (e as Error).message }); }
    finally { setBusy(false); }
  };

  const runAction = async (a: ActionDef) => {
    if (!d || !key) return;
    if (a.confirm && !window.confirm(a.confirm)) return;
    setBusy(true); setMsg(null);
    try {
      const text = a.kind === 'code' ? await CODE_ACTIONS[a.name]?.(draft) : (await api.action(d.entity, key, a.name), `${a.label} done`);
      await qc.invalidateQueries({ queryKey: ['rec', d.entity, key] }); qc.invalidateQueries({ queryKey: ['list', screenId] });
      setMsg({ design: 'Positive', text: text ?? `${a.label} done` });
    } catch (e) { setMsg({ design: 'Negative', text: (e as Error).message }); }
    finally { setBusy(false); }
  };

  if (def.isLoading || (!isNew && rec.isLoading) || meta.isLoading) return <FlexBox justifyContent="Center" style={{ padding: '3rem' }}><BusyIndicator active /></FlexBox>;
  if (def.error || !d) return <MessageStrip design="Negative" hideCloseButton>{(def.error as Error)?.message ?? 'Screen not found'}</MessageStrip>;
  if (rec.error) return <MessageStrip design="Negative" hideCloseButton>{(rec.error as Error).message}</MessageStrip>;

  const title = mode === 'create' ? `New ${d.title.replace(/s$/, '')}` : String(draft[d.form.titleField ?? keyField ?? ''] ?? '');
  const subtitle = mode === 'create' ? d.entity : String(draft[d.form.subtitleField ?? ''] ?? '');
  const headerFacts = (d.form.headerFields ?? []).map(f => ({ f, mf: findField(meta.data, f) }));

  const renderField = (fd: FieldDef, m: EntityMeta | undefined) => {
    if (fd.displayOnly && mode !== 'display') return null;
    const mf = findField(m, fd.field);
    return (
      <FormItem key={fd.field} labelContent={<Label required={fd.required && mode !== 'display'} showColon>{labelFor(fd.field, mf, fd.label)}</Label>}>
        <FieldControl def={fd} meta={m} mf={mf} value={draft[fd.field]} record={draft} mode={mode} onChange={patch} />
      </FormItem>
    );
  };

  const actionButtons = <>
    {mode === 'display' && <>
      <Button onClick={() => nav(`/s/${screenId}`)}>Back</Button>
      {canEdit && d.form.canEdit && <Button icon="edit" onClick={() => setMode('edit')}>Edit</Button>}
      {canEdit && d.actions?.filter(a => when(a.when, draft)).map(a => <Button key={a.name} icon={a.icon} design={a.design ?? 'Default'} disabled={busy} onClick={() => runAction(a)}>{a.label}</Button>)}
    </>}
    {mode !== 'display' && <>
      <Button onClick={() => { if (mode === 'create') nav(`/s/${screenId}`); else { setDraft(rec.data ?? {}); setMode('display'); } }}>Cancel</Button>
      <Button design="Emphasized" icon="save" disabled={busy} onClick={save}>{busy ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}</Button>
    </>}
  </>;

  const sections = [
    ...d.form.sections.map((s, i) => (
      <ObjectPageSection key={`s${i}`} id={`s${i}`} titleText={s.title ?? (s.kind === 'collection' ? s.collection : `Section ${i + 1}`)}>
        {s.kind === 'fields'
          ? <Form layout={`S1 M${s.columns ?? 2} L${s.columns ?? 2} XL${s.columns ?? 2}`} labelSpan="S12 M4 L4 XL4">{s.fields.map(f => renderField(f, meta.data))}</Form>
          : <CollectionTable section={s} meta={meta.data} rows={(draft[s.collection] as Rec[] | undefined) ?? []} mode={mode} currency={currency} onChange={rows => patch({ [s.collection]: rows })} />}
      </ObjectPageSection>
    )),
    // Attachments belong to a saved record (they need its key), so they are not shown while creating one.
    ...(!isNew && key ? [(
      <ObjectPageSection key="attachments" id="attachments" titleText="Attachments">
        <AttachmentsPanel set={d.entity} recordKey={key} canEdit={canEdit} />
      </ObjectPageSection>
    )] : []),
  ];

  return (
    <ObjectPage style={{ height: '100%' }} hidePinButton={isMobile}
      titleArea={<ObjectPageTitle header={<Title level="H3">{title}</Title>} subHeader={<Text>{subtitle}</Text>}
        // Phone: actions move to a footer bar (Fiori pattern) so the title keeps the full width and buttons can wrap.
        actionsBar={isMobile ? undefined : <FlexBox style={{ gap: '0.5rem' }} wrap="Wrap">{actionButtons}</FlexBox>} />}
      footerArea={isMobile
        ? <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '0.5rem', padding: '0.5rem 0.75rem', background: 'var(--sapPageFooter_Background,#fff)', borderTop: '1px solid var(--sapPageFooter_BorderColor,#d9d9d9)', boxShadow: '0 -0.125rem 0.5rem rgba(0,0,0,0.08)' }}>{actionButtons}</div>
        : undefined}
      headerArea={<ObjectPageHeader>
        {msg && <MessageStrip design={msg.design} onClose={() => setMsg(null)} style={{ marginBottom: '0.75rem' }}>{msg.text}</MessageStrip>}
        {mode === 'display' && headerFacts.length > 0 && (
          // Phone: a two-column grid so long labels wrap inside their cell instead of pushing past the edge.
          <div style={isMobile
            ? { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.75rem 1rem' }
            : { display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
            {headerFacts.map(({ f, mf }) => {
              const col = d.list.columns.find(c => c.field === f);
              const v = draft[f];
              return (
                <FlexBox key={f} direction="Column" style={{ minWidth: 0 }}><Label wrappingType="Normal">{labelFor(f, mf, col?.label)}</Label>
                  {col?.format === 'status' ? <ObjectStatus state={col.statusMap?.[String(v)] ?? 'None'}>{enumLabel(String(v ?? ''))}</ObjectStatus>
                    : <span style={{ overflowWrap: 'anywhere' }}>{formatValue(v, col ?? { field: f }, mf, col?.format === 'money' ? currency : undefined) || '—'}</span>}
                </FlexBox>);
            })}
          </div>)}
      </ObjectPageHeader>}>
      {sections}
    </ObjectPage>
  );
}
